// components/molecules/NoteEditor/NoteEditor.tsx
import React, { ReactNode, useRef, useState, useEffect, useCallback } from 'react';
import { Box, TextField, Tabs, Tab, Typography, Paper, Fade } from '@mui/material';
import { editIcon, FaOptionIcon, informationIcon, previewIcon } from '@europrocurement/l2d-icons';
import { NoteContent } from '../../atoms/NoteContent/NoteContent';
import { TabPanel } from './TabPanel';

export interface NoteEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: string;
    rows?: number;
}

/**
 * Composant d'édition de notes avec support Markdown et aperçu en temps réel.
 * Fournit une interface à onglets permettant l'édition et la prévisualisation du contenu.
 */
export const NoteEditor: React.FunctionComponent<NoteEditorProps> = function ({
    value,
    onChange,
    placeholder = '...',
    disabled = false,
    error,
    rows = 6,
}) {
    const [activeTab, setActiveTab] = React.useState(0);
    const [hasHiddenContent, setHasHiddenContent] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const textFieldRef = useRef<HTMLTextAreaElement>(null);

    /**
     * Gère le changement d'onglet entre édition et aperçu.
     */
    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    /**
     * Vérifie si le contenu de la zone de texte déborde de l'espace visible
     * et détermine la position du scroll pour afficher les indicateurs appropriés.
     */
    const checkScrollableContent = useCallback(() => {
        const textarea = textFieldRef.current;
        if (!textarea) return;

        const hasOverflow = textarea.scrollHeight > textarea.clientHeight;
        const isScrolledToBottom =
            Math.abs(textarea.scrollHeight - textarea.clientHeight - textarea.scrollTop) < 1;

        setHasHiddenContent(hasOverflow);
        setIsAtBottom(isScrolledToBottom);
    }, []);

    /**
     * Gestionnaire d'événement pour le scroll de la zone de texte.
     * Met à jour les indicateurs en temps réel.
     */
    const handleScroll = useCallback(() => {
        checkScrollableContent();
    }, [checkScrollableContent]);

    /**
     * Effect pour surveiller les changements de contenu et attacher
     * l'écouteur de scroll directement sur le textarea.
     */
    useEffect(() => {
        checkScrollableContent();

        const textarea = textFieldRef.current;

        if (!textarea) return undefined;

        // Attacher l'écouteur directement sur l'élément textarea
        textarea.addEventListener('scroll', handleScroll);

        // Nettoyer l'écouteur au démontage
        return () => {
            textarea.removeEventListener('scroll', handleScroll);
        };
    }, [value, handleScroll, checkScrollableContent]);

    /**
     * Détermine le message et la couleur de l'indicateur de contenu
     * basé uniquement sur la position du scroll et la présence de contenu caché.
     */
    const getContentIndicator = () => {
        // Pas d'indicateur si tout le contenu est visible
        if (!hasHiddenContent) return null;

        // Si on a scrollé jusqu'en bas, on affiche "Bas de la note"
        if (isAtBottom) {
            return {
                message: '✓ Bas de la note',
                color: 'primary.main',
                textColor: 'success.contrastText',
            };
        }

        // Sinon, on indique qu'il y a plus de contenu en bas
        return {
            message: '↓ Plus de contenu',
            color: 'primary.main',
            textColor: 'primary.contrastText',
        };
    };

    const contentIndicator = getContentIndicator();

    const editTabLabel: ReactNode = (
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'start',
                alignItems: 'start',
                gap: 1,
            }}
        >
            <FaOptionIcon {...editIcon.props} />
            <Box>Éditer</Box>
        </Typography>
    );

    const previewTabLabel: ReactNode = (
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'start',
                alignItems: 'start',
                gap: 1,
            }}
        >
            <FaOptionIcon {...previewIcon.props} />
            <Box>Aperçu</Box>
        </Typography>
    );

    return (
        <Box sx={{ width: '100%' }}>
            <Paper
                variant="outlined"
                sx={{ overflow: 'hidden', position: 'relative' }}
            >
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                        backgroundColor: 'grey.50',
                        minHeight: 40,
                        '& .MuiTab-root': {
                            minHeight: 40,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                        },
                    }}
                >
                    <Tab label={editTabLabel} />
                    <Tab label={previewTabLabel} />
                </Tabs>

                <Box sx={{ minHeight: 200, position: 'relative' }}>
                    <TabPanel
                        value={activeTab}
                        index={0}
                    >
                        <TextField
                            multiline
                            rows={rows}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder={placeholder}
                            disabled={disabled}
                            error={!!error}
                            helperText={error}
                            fullWidth
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                inputRef: textFieldRef,
                                sx: {
                                    padding: '16px',
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                },
                            }}
                        />

                        {/* Indicateur de statut du contenu (débordement/fin) */}
                        <Fade in={contentIndicator !== null && activeTab === 0}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    right: 8,
                                    backgroundColor: contentIndicator?.color || 'primary.main',
                                    color: contentIndicator?.textColor || 'primary.contrastText',
                                    borderRadius: '12px',
                                    padding: '4px 8px',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    boxShadow: 2,
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                    transition: 'all 0.3s ease-in-out',
                                }}
                            >
                                {contentIndicator?.message}
                            </Box>
                        </Fade>
                    </TabPanel>

                    <TabPanel
                        value={activeTab}
                        index={1}
                    >
                        <Box sx={{ padding: '16px', minHeight: rows * 24 }}>
                            {value ? (
                                <NoteContent content={value} />
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontStyle="italic"
                                >
                                    Aucun contenu à prévisualiser
                                </Typography>
                            )}
                        </Box>
                    </TabPanel>
                </Box>
            </Paper>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'start',
                    alignItems: 'start',
                    gap: 1,
                }}
            >
                <FaOptionIcon {...informationIcon.props} />
                <Box>
                    Supporte le <strong>Markdown</strong> : **gras**, *italique*, `code`, # titres,
                    - listes, &gt; citations, [liens](url)
                </Box>
            </Typography>
        </Box>
    );
};
